import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { thingsboardService } from '../services/thingsboard';
import { alarmWsManager } from '../services/websocket';

export interface AgentMessage {
  id: string;
  type: 'info' | 'warning';
  originalType: string;
  title: string;
  description: string;
  createdTime: number;
  isRead: boolean;
}

const ALLOWED_ALARM_TYPES = ['Absence Alarm', 'Current Leakage Alarm', 'high co2', 'high humidity', 'low humidity'];

export function useAlarms(assetId?: string | null) {
  const { t, i18n } = useTranslation();
  const [rawAlarms, setRawAlarms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeCustomerId, setActiveCustomerId] = useState<string | undefined>();

  const mapAlarmToMessage = useCallback((alarm: any): AgentMessage => {
    const severity = alarm.severity || 'INDETERMINATE';
    const type: 'info' | 'warning' = (severity === 'CRITICAL' || severity === 'MAJOR' || severity === 'WARNING') ? 'warning' : 'info';
    
    let title = alarm.details?.subject || alarm.type || t('alarms.default_label');
    let description = alarm.details?.message || alarm.name || t('alarms.no_details');

    // Custom mapping based on alarm type
    if (alarm.type === 'Absence Alarm') {
      title = t('alarms.absence_title');
      const startTs = alarm.startTs || alarm.createdTime;
      if (startTs) {
        const date = new Date(startTs - (4 * 60 * 60 * 1000)); // 4 hours before
        const formattedTime = new Intl.DateTimeFormat(i18n.language, {
          month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric'
        }).format(date);
        description = t('alarms.absence_desc', { formattedTime });
      }
    } else if (alarm.type === 'Current Leakage Alarm') {
      const originatorName = alarm.originatorName || t('alarms.unknown');
      title = t('alarms.leakage_title', { originatorName });
      description = t('alarms.leakage_desc');
    } else if (alarm.type === 'high humidity') {
      const originatorName = alarm.originatorName || t('alarms.unknown');
      title = t('alarms.high_humidity_title');
      description = t('alarms.high_humidity_desc', { originatorName });
    } else if (alarm.type === 'low humidity') {
      const originatorName = alarm.originatorName || t('alarms.unknown');
      title = t('alarms.low_humidity_title', { originatorName });
      description = t('alarms.low_humidity_desc', { originatorName });
    } else if (alarm.type === 'high co2') {
      const originatorName = alarm.originatorName || t('alarms.unknown');
      title = t('alarms.high_co2_title', { originatorName });
      description = t('alarms.high_co2_desc', { originatorName });
    }

    return {
      id: alarm.id.id,
      type,
      originalType: alarm.type,
      title,
      description,
      createdTime: alarm.createdTime || Date.now(),
      isRead: alarm.status ? alarm.status.endsWith('ACK') : (alarm.acknowledged || false)
    };
  }, [t, i18n.language]);

  const messages = useMemo(() => {
    return rawAlarms.map(mapAlarmToMessage);
  }, [rawAlarms, mapAlarmToMessage]);

  const fetchInitialAlarms = useCallback(async (isBackground = false, customerId?: string) => {
    if (!isBackground) setLoading(true);
    try {
      const result = (customerId && customerId !== '13814000-1dd2-11b2-8080-808080808080')
        ? await thingsboardService.getCustomerAlarms(customerId, 50, 0, 'ACTIVE')
        : await thingsboardService.getAlarms(50, 0, 'ACTIVE');
        
      if (result && result.data) {
        // Filter for allowed types
        const filtered = result.data.filter((a: any) => ALLOWED_ALARM_TYPES.includes(a.type));
        
        setRawAlarms(prev => {
          if (prev.length !== filtered.length) return filtered;
          const isSame = prev.every((p, i) => p.id?.id === filtered[i]?.id?.id && p.type === filtered[i]?.type);
          return isSame ? prev : filtered;
        });
      }
    } catch (err) {
      console.error('Failed to fetch initial alarms', err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [mapAlarmToMessage]);

  const acknowledgeAlarm = useCallback(async (alarmId: string) => {
    try {
      await thingsboardService.acknowledgeAlarm(alarmId);
      setRawAlarms(prev => prev.map(a => a.id.id === alarmId ? { ...a, status: 'ACTIVE_ACK' } : a));
    } catch (err) {
      console.error('Failed to acknowledge alarm', err);
    }
  }, []);

  const clearAlarm = useCallback(async (alarmId: string) => {
    try {
      await thingsboardService.clearAlarm(alarmId);
      setRawAlarms(prev => prev.filter(a => a.id.id !== alarmId));
    } catch (err) {
      console.error('Failed to clear alarm', err);
    }
  }, []);

  useEffect(() => {
    // We move fetchInitialAlarms inside setupAlarmsWS to ensure we have the customerId
    
    // Use WebSocket subscription for alarms
    let subId: number | null = null;
    
    const setupAlarmsWS = async () => {
      console.log(`[useAlarms] Setting up alarms WS for asset: ${assetId}`);
      try {
        let customerId: string | undefined;

        if (assetId) {
          // If assetId is provided, get the customer associated with that asset
          const asset = await thingsboardService.getAsset(assetId);
          customerId = asset.customerId?.id;
          console.log(`[useAlarms] Found customerId for asset ${assetId}: ${customerId}`);
        } else {
          // Fallback to current logged-in user's customerId
          console.log('[useAlarms] No assetId, falling back to user info');
          const user = await thingsboardService.getUser();
          customerId = user.customerId?.id;
          console.log(`[useAlarms] Found customerId from user: ${customerId}`);
        }
        
        if (customerId && customerId !== '13814000-1dd2-11b2-8080-808080808080') {
          setActiveCustomerId(customerId);
          // Fetch initial alarms for this specific customer
          console.log(`[useAlarms] Fetching initial alarms for customer ${customerId}`);
          fetchInitialAlarms(false, customerId);

          console.log(`[useAlarms] Connecting to alarm WebSocket for customer ${customerId}`);
          alarmWsManager.connect();
          subId = alarmWsManager.subscribeAlarms(customerId, (update: any) => {
            // Handle the new nested structure from ALARM_DATA or fallback
            const alarmsData = update.data?.data || update.data;
            
            if (alarmsData && Array.isArray(alarmsData)) {
              const newAlarms = alarmsData.filter((a: any) => ALLOWED_ALARM_TYPES.includes(a.type));
              setRawAlarms(prev => {
                const alarmMap = new Map(prev.map(a => [a.id.id, a]));
                newAlarms.forEach((a: any) => {
                   if (a.status === 'ACTIVE_UNACK' || a.status === 'ACTIVE_ACK') {
                      alarmMap.set(a.id.id, a);
                   } else {
                      alarmMap.delete(a.id.id);
                   }
                });
                const newArray = Array.from(alarmMap.values()).sort((a,b) => (b.createdTime || 0) - (a.createdTime || 0));
                
                if (prev.length !== newArray.length) return newArray;
                const isSame = prev.every((p, i) => p.id?.id === newArray[i]?.id?.id && p.type === newArray[i]?.type);
                return isSame ? prev : newArray;
              });
            } else if (update.update && Array.isArray(update.update)) {
               const a = update.update[0];
               setRawAlarms(prev => {
                  const existing = prev.find(m => m.id?.id === a.id?.id);
                  const merged = existing ? { ...existing, ...a } : a;
                  
                  if (merged && ALLOWED_ALARM_TYPES.includes(merged.type)) {
                     const filtered = prev.filter(m => m.id?.id !== merged.id?.id);
                     if (merged.status === 'ACTIVE_UNACK' || merged.status === 'ACTIVE_ACK') {
                        return [merged, ...filtered];
                     }
                     return filtered.length === prev.length ? prev : filtered;
                  }
                  return prev;
               });
            }
          });
        } else {
          console.log('[useAlarms] No valid customerId found, skipping WebSocket subscription');
        }
      } catch (err) {
        console.error('[useAlarms] Failed to setup alarms websocket', err);
      }
    };

    setupAlarmsWS();

    return () => {
      alarmWsManager.unsubscribe(subId);
    };
  }, [fetchInitialAlarms, mapAlarmToMessage, assetId]);

  return { messages, loading, refetch: () => fetchInitialAlarms(false, activeCustomerId), acknowledge: acknowledgeAlarm, clear: clearAlarm };
}
